import React, { useState, useContext } from 'react'

import { FieldEmail } from 'components/Fields/Email'
import { useSendMagicAuthCode, useVerifyMagicAuthCode } from 'hooks/api/useWorkOSAuth'
import { workosAuthService } from 'services/workos-auth.service'
import { getEmbeddedKey } from 'utils'
import { CodeExpiryNotice } from 'components/auth'
import { OTP_CODE_TTL_SECONDS, useCodeExpiry } from 'hooks/useCodeExpiry'

import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { Stack, Typography, Button, TextField, Box, Divider } from '@mui/material'
import { CompanyContext } from 'contexts/companyContext'
import { TwoFactorChallenge } from 'components/auth'
import type { AuthFactorType } from '@repo/types'

interface SignInProps {
  handleClose?: () => void
  inDrawer?: boolean
  title?: string
  height?: string
}

export const SignIn = ({ title = 'Sign in', height = '100%', inDrawer = false, handleClose = () => {} }: SignInProps) => {
  const methods = useForm()
  const navigate = useNavigate()
  const { handleSubmit } = methods
  methods.watch('email')

  const [email, setEmail] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [codeSent, setCodeSent] = useState<boolean>(false)
  // VRG-720 — send time of the outstanding code, for its visible deadline.
  const [codeIssuedAt, setCodeIssuedAt] = useState<number | null>(null)
  const { expired: codeExpired } = useCodeExpiry(codeIssuedAt, OTP_CODE_TTL_SECONDS)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // VRG-490 §2 — a verified second factor withholds the session; see the
  // identical branch in SignInBanner (this drawer's non-embedded sibling).
  const [challenge, setChallenge] = useState<{ ticket: string; factorType: AuthFactorType } | null>(null)

  const sendMagicAuthCode = useSendMagicAuthCode()
  const verifyMagicAuthCode = useVerifyMagicAuthCode()

  const { companyId } = useContext(CompanyContext);

  const handleSendCode = handleSubmit(async (data) => {
    setError(null)
    try {
      await sendMagicAuthCode.mutateAsync({ email: data.email, companyId })
      setEmail(data.email)
      setCodeSent(true)
      setCodeIssuedAt(Date.now())
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to send code. Please try again.')
    }
  })

  /*
   * VRG-720 — a real RESEND, which this page did not have.
   *
   * Its only escape from a dead code was "Change Email", which drops back to
   * the email step and makes you retype an address you did not want to change.
   * That is a go-back button standing in for a resend — the same defect
   * VERA-837 fixed on OnboardingAuthPanel, still present here.
   *
   * It calls the mutation directly off the captured `email` rather than
   * reusing `handleSendCode`: that one is `handleSubmit`-wrapped and re-reads
   * the form, whose field is not rendered on this step. Going through the
   * state we already hold is unambiguous.
   */
  const handleResendCode = async () => {
    if (isResending || !email) return
    setIsResending(true)
    setError(null)
    try {
      await sendMagicAuthCode.mutateAsync({ email, companyId })
      setCode('')
      setCodeIssuedAt(Date.now())
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to send code. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code')
      return
    }

    setError(null)
    try {
      console.log('[SignIn] handleVerifyCode called');
      console.log('[SignIn] Email:', email);
      console.log('[SignIn] Code length:', code.length);

      const embeddKey = getEmbeddedKey()
      console.log('[SignIn] Retrieved embeddKey:', embeddKey);
      console.log('[SignIn] embeddKey type:', typeof embeddKey);
      console.log('[SignIn] Calling verifyMagicAuthCode with:', { email, code, embeddKey: embeddKey || undefined });

      const result = await verifyMagicAuthCode.mutateAsync({ email, code, embeddKey: embeddKey || undefined, companyId })

      // VRG-490 §2 — a verified second factor withholds the session. This
      // mutation resolves (doesn't throw) on a challengeRequired response
      // too, since the primary factor genuinely checked out — without this
      // branch the drawer would close and navigate with no token stored.
      if (result.challengeRequired && result.ticket && result.factorType) {
        setChallenge({ ticket: result.ticket, factorType: result.factorType })
        return
      }

      // Navigate to certificates after successful authentication
      handleClose()
      navigate('/certificates')
    } catch (err: any) {
      const errorData = err?.response?.data
      const errorCode = errorData?.code || (typeof errorData?.message === 'object' ? errorData?.message?.code : null)
      if (errorCode === 'COMPANY_SUSPENDED' || (typeof errorData?.message === 'string' && errorData?.message?.includes('suspended'))) {
        navigate('/company-suspended')
        return
      }
      setError(errorData?.message || 'Invalid code. Please try again.')
    }
  }

  const handleGoogleSignIn = () => {
    // Get embedd context from sessionStorage or URL params if available
    const embeddKey = sessionStorage.getItem('embeddKey')
    const companyId = sessionStorage.getItem('companyId')

    workosAuthService.loginWithGoogle({
      embeddKey: embeddKey || undefined,
      companyId: companyId || undefined,
    })
  }

  return (
    <Stack
      sx={theme => ({
        alignItems: 'center',
        padding: inDrawer ? '0' : '24px 16px',
        height: height,
        textAlign: 'center',
        overflow: 'auto',
        flex: 1,
        /*
         * A SURFACE, so it takes a surface token — not `primary.main`.
         *
         * This painted the whole sign-in drawer with the ACCENT while its
         * contents render in `text.heading` / `text.primary` (both black) and
         * its CTAs are `variant="contained"` (also filled from the accent). That
         * is only legible for a mid-tone accent, and it was already poor at
         * ~3.2:1 with Vera purple. With the base accent now black
         * (`settings/mui/palette.ts`) it composes black text and black buttons
         * on a black ground — invisible — and it would do the same today for any
         * tenant whose `primary_color` is dark.
         *
         * So this is a latent category error (accent used as a surface) that the
         * accent change EXPOSED, not one it introduced. The fix is the token,
         * not the colour: `background.paper` is what a drawer surface is, and it
         * stays correct at every tenant accent.
         */
        background: theme.palette.background.paper,
      })}
    >
      {challenge ? (
        // VRG-490 §2 — replaces the whole email/code + Google block: Google
        // sign-in has nothing to do with a second factor, and the component
        // renders its own instructions, input and action buttons.
        <Stack sx={{ width: '100%', maxWidth: '400px' }}>
          <TwoFactorChallenge
            ticket={challenge.ticket}
            factorType={challenge.factorType}
            onBack={() => {
              setChallenge(null)
              setCodeSent(false)
              setCode('')
              setError(null)
            }}
            onVerified={() => {
              setChallenge(null)
              handleClose()
              navigate('/certificates')
            }}
          />
        </Stack>
      ) : (
        <>
          <Stack sx={{ flex: '1 1 auto', width: '100%', maxWidth: '400px' }}>
            {!inDrawer && (
              <Typography variant="h2" mb="8px" sx={{ textTransform: 'uppercase' }}>
                {title}
              </Typography>
            )}

            {/* Google Sign In Button */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleGoogleSignIn}
              sx={{ mb: 2, bgcolor: 'white', color: 'black', '&:hover': { bgcolor: '#f5f5f5' } }}
              disabled={sendMagicAuthCode.isPending || verifyMagicAuthCode.isPending}
            >
              Sign in with Google
            </Button>

            <Divider sx={{ my: 2 }}>OR</Divider>

            {/* Magic Auth (Email + Code) */}
            {!codeSent ? (
              <>
                <Typography variant="body1" mb={3} px="24px">
                  Enter your email to receive a 6-digit verification code
                </Typography>
                <FieldEmail label="Email" {...methods} />
                {error && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="body1" mb="16px" px="24px">
                  Enter the 6-digit code sent to {email}
                </Typography>
                <TextField
                  label="Verification Code"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setCode(value)
                  }}
                  inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <CodeExpiryNotice
                  issuedAt={codeIssuedAt}
                  ttlSeconds={OTP_CODE_TTL_SECONDS}
                  resendLabel='Use Resend Code below'
                />
                {error && (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {error}
                  </Typography>
                )}
              </>
            )}
          </Stack>

          <Stack sx={{ width: '100%', maxWidth: '400px', marginTop: '20px' }}>
            {codeSent ? (
              <>
                <Button
                  // VRG-720: Resend takes the fill once the code has expired.
                  // Verify stays clickable — the countdown predicts the
                  // server's answer rather than being it. See the fuller note
                  // in OnboardingAuthPanel.
                  variant={codeExpired ? 'outlined' : 'contained'}
                  fullWidth
                  onClick={handleVerifyCode}
                  disabled={verifyMagicAuthCode.isPending || code.length !== 6}
                  sx={{ mb: 1 }}
                >
                  {verifyMagicAuthCode.isPending ? 'Verifying...' : 'Verify Code'}
                </Button>
                <Button
                  variant={codeExpired ? 'contained' : 'outlined'}
                  fullWidth
                  onClick={handleResendCode}
                  disabled={verifyMagicAuthCode.isPending || isResending}
                  sx={{ mb: 1 }}
                >
                  {isResending ? 'Sending...' : 'Resend Code'}
                </Button>
                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setCodeSent(false)
                    setCode('')
                    setCodeIssuedAt(null)
                    setError(null)
                  }}
                  disabled={verifyMagicAuthCode.isPending}
                >
                  Change Email
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={handleSendCode}
                disabled={!methods.getValues('email') || sendMagicAuthCode.isPending}
              >
                {sendMagicAuthCode.isPending ? 'Sending...' : 'Send Code'}
              </Button>
            )}
          </Stack>
        </>
      )}
    </Stack>
  )
}
